/**
 * InventoryDialog — A standalone dialog that hosts `InventoryTable` for
 * any given workspace nodeId. Reused by:
 *   • OSEListsAggregator (open one list from the aggregated directory)
 *   • RestaurantDetailDialog (the OS&E / Inventories buttons)
 *
 * The dialog has no per-list selector — it always opens scoped to the
 * passed nodeId so the user can immediately see / edit rows. Saved-list
 * tabs WITHIN the node still work (the InventoryTable manages those).
 */
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { X, Package } from 'lucide-react';
import InventoryTable from './InventoryTable';

export default function InventoryDialog({ open, onClose, nodeId, nodeLabel, headerAccent = '#003DA5' }) {
  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="inventory-dialog"
        className="max-w-7xl w-[97vw] max-h-[92vh] p-0 overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{nodeLabel || 'OS&E list'}</DialogTitle>
        <DialogDescription className="sr-only">
          Operating supplies &amp; equipment list for {nodeLabel || nodeId}.
        </DialogDescription>
        <div
          className="px-5 py-3 flex items-center gap-3 flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${headerAccent} 0%, ${headerAccent}D0 100%)` }}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
            <Package className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/85" style={{ fontFamily: 'Manrope' }}>
              OS&amp;E · Inventories
            </p>
            <h2 className="text-base font-bold text-white truncate" style={{ fontFamily: 'Manrope' }}>
              {nodeLabel || 'List'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-white hover:bg-white/15" data-testid="inventory-dialog-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4" style={{ background: '#FAF8F2' }}>
          <InventoryTable nodeId={nodeId} nodeLabel={nodeLabel || nodeId} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
